/**
 * Merchant text and emoji as sprites.
 *
 * ── Why this is not `[...text]` ────────────────────────────────────────────
 *
 * Spreading a string splits it by CODE POINT, and an emoji is very often more
 * than one. `👨‍👩‍👧` is five code points joined by zero-width joiners; a flag is
 * two regional indicators; `👍🏽` is a base plus a skin-tone modifier. Split by
 * code point and the merchant types one family and gets three disembodied
 * people and two invisible characters falling down their storefront.
 *
 * `Intl.Segmenter` with grapheme granularity is the correct tool and is built
 * into every browser that runs a Shopify storefront (Safari 14.1+), so it costs
 * zero bundle bytes. The fallback exists only so an ancient browser degrades to
 * "slightly wrong emoji" rather than to a thrown error on boot.
 */

/** Atlas columns are per glyph, so this is a budget, not a preference. */
export const MAX_GLYPHS = 6;

export function splitGlyphs(text: string, max = MAX_GLYPHS): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  let parts: string[];
  const Seg = (globalThis as { Intl?: { Segmenter?: typeof Intl.Segmenter } }).Intl?.Segmenter;
  if (Seg) {
    parts = [...new Seg(undefined, { granularity: 'grapheme' }).segment(trimmed)].map(
      (s) => s.segment
    );
  } else {
    parts = [...trimmed];
  }

  // Whitespace between glyphs is a separator, not a sprite: a blank atlas cell
  // renders as a particle that is there, costs a slot, and paints nothing.
  return parts.filter((g) => g.trim().length > 0).slice(0, max);
}
